import { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'clsx';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import {
  FAULTCODE_BIRTH,
  FAULTCODE_USERNAME_EXISTS,
} from 'src/common/constants/faultCodes';
import {
  BIRTH_YEAR,
  BIRTH_YEAR_REQUIRED,
  NEXT,
  SIGN_UP,
  SIGN_UP_FAILED,
  USERNAME_EMAIL_REQUIRED,
} from 'src/common/constants/localizations/auth';
import {
  EMAIL_ADDRESS,
  EMAIL_ALREADY_EXISTS,
  FEMALE_LABEL,
  MALE_LABEL,
  NAME,
  NONBINARY_LABEL,
  PASSWORD,
  PASSWORD_REQUIRED,
  PREFERNOTTOSAY_LABEL,
} from 'src/common/constants/localizations/shared';
import { SOURCE_ONAIR } from 'src/common/constants/sourceQueryParams';
import { storeFakeAuthData } from '../../actions/fakeAuth';
import { logSignUpClick } from '../../actions/logging';
import Input from '../shared/form/Input';
import { RadioButton } from '../shared/form/RadioButton';
import Checkmark from '../shared/svgIcons/Checkmark';
import CoralButton from '../shared/button/CoralButton';
import TopLevelErrorContainer from '../shared/form/TopLevelErrorContainer';
import {
  fbPixelCustomEvents,
  fbPixelStandardEvents,
  fbTrack,
} from '../../utils/fbTrack';
import numericInputNormalizer from '../../utils/form/normalizers/numericInput';
import getRequiredLabel from '../../utils/form/getRequiredLabel';
import checkFieldsForValidInput from '../utils/checkFieldsForValidInput';
import viewTypes from '../../constants/auth/viewTypes';
import account from '../../constants/analytics/categoryActionLabel/account';
import {
  inputCheckmarkSvgProps,
  inputMappedPartnerAuthStyles,
} from '../shared/styles/partnerAuthSharedStyles';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import {
  baseError,
  composeValidators,
  isRequired,
  isValidBirthYear,
  isValidEmail,
  isValidFullName,
  isValidPassword,
} from '../../utils/form/validation';
import { BIRTH_YEAR_TOO_YOUNG } from '../../constants/localizations/auth';
import AuthSectionDivider from './shared/AuthSectionDivider';
import getPartnerParams from './oauthProvider/getPartnerParams';
import OauthButtons from './shared/OauthButtons';
import GeneralNotices from './shared/GeneralNotices';
import SecondaryContent from './shared/SecondaryContent';

import authCss from './auth.module.scss';
import css from './sign-up-view.module.scss';

const fieldOrder = ['name', 'email', 'password', 'birthYear'];
const hintStyle = { fontSize: '16px' };

const svgProps = {
  ...inputCheckmarkSvgProps,
  className: authCss.checkmark,
};

// exported for testing
export const RadioButtonWrapper = ({ input = {}, label, dataTestId }) => (
  <RadioButton
    {...input}
    defaultChecked={input.checked}
    label={label}
    dataTestId={dataTestId}
    handleClick={(evt) => {
      input.onChange(evt.target.value);
    }}
  />
);

class SignUpView extends Component {
  static propTypes = {
    actions: PropTypes.shape({
      createAccount: PropTypes.func.isRequired,
      createAccountWithOauth: PropTypes.func.isRequired,
      trackSignUpPage: PropTypes.func.isRequired,
    }).isRequired,
    fakeAuthInfo: PropTypes.object.isRequired,
    logSignUpClick: PropTypes.func.isRequired,
    storeAuthData: PropTypes.func.isRequired,
    handleChangeToSignIn: PropTypes.func.isRequired,
    viewTypeData: PropTypes.object.isRequired,
    failAuthNotice: PropTypes.string,
    isDialog: PropTypes.bool,
    isPartner: PropTypes.bool,
    source: PropTypes.string,
    isPaymentFlow: PropTypes.bool,
  };

  static contextType = LocationAndLocalizationContext;

  constructor(props) {
    super(props);
    this.handleOAuthSignUp = this.handleOAuthSignUp.bind(this);
  }

  componentDidMount() {
    fbTrack(fbPixelCustomEvents.SignUpPageVisit);
    this.props.actions.trackSignUpPage();
  }

  handleOAuthSignUp(...oauthData) {
    const { actions, viewTypeData } = this.props;
    actions.createAccountWithOauth(...oauthData, { viewTypeData }).then(() => {
      fbTrack(fbPixelStandardEvents.CompleteRegistration);
    });
  }

  onSubmit = async (values, form) => {
    const { actions, isPartner, source, storeAuthData, viewTypeData } =
      this.props;
    const { location } = this.context;
    const { name, password, email, birthYear, gender } = values;

    fbTrack(fbPixelStandardEvents.CompleteRegistration);
    this.props.logSignUpClick(account.labels.email, source);

    try {
      await actions.createAccount({
        username: email?.trim?.(),
        password,
        firstName: name,
        birth: birthYear,
        gender: gender === null ? '' : gender,
        source,
        params: getPartnerParams(isPartner, location),
        viewTypeData,
      });

      storeAuthData({
        name,
        password,
        email,
        birthYear,
        gender,
        viewType: viewTypes.signUp,
      });
      form.restart();
    } catch (error) {
      const responseData = error.response?.data;
      switch (responseData.faultCode) {
        case FAULTCODE_USERNAME_EXISTS:
          return baseError(EMAIL_ALREADY_EXISTS, [email]);
        case FAULTCODE_BIRTH:
          return baseError(BIRTH_YEAR_TOO_YOUNG);
        default:
          return baseError(SIGN_UP_FAILED);
      }
    }
  };

  render() {
    const {
      handleChangeToSignIn,
      failAuthNotice,
      isDialog,
      isPartner,
      source,
      isPaymentFlow,
      fakeAuthInfo,
    } = this.props;

    const { name, email, birthYear, password } = fakeAuthInfo;

    const { getLocalizedText, location } = this.context;
    const isOnAirFlow = source === SOURCE_ONAIR;

    const additionalFieldProps = isPartner
      ? {
          ...inputMappedPartnerAuthStyles,
          floatingLabel: true,
          localizeErrorMessages: true,
          inlineErrors: true,
        }
      : {
          hintStyle,
          isBold: true,
        };

    return (
      <Form
        onSubmit={this.onSubmit}
        initialValues={{
          gender: fakeAuthInfo.gender || null,
        }}
      >
        {({
          handleSubmit,
          errors,
          pristine,
          touched,
          hasValidationErrors,
          submitting,
          submitSucceeded,
          submitErrors,
        }) => {
          const validFieldsMap = isPartner
            ? checkFieldsForValidInput(touched, {
                ...errors,
                ...submitErrors,
              })
            : null;

          return (
            <form onSubmit={handleSubmit}>
              <div
                data-testid="signUp"
                className={authCss.containerRestrictions}
              >
                {!isOnAirFlow && (
                  <OauthButtons
                    actionHandler={this.handleOAuthSignUp}
                    loggingHandler={this.props.logSignUpClick}
                    isDialog={isDialog}
                    isSignUp
                    isPaymentFlow={isPaymentFlow}
                    isPartner={isPartner}
                    source={source}
                    partnerParams={getPartnerParams(isPartner, location)}
                  />
                )}
                <GeneralNotices
                  key="signInGeneralNotices"
                  failAuthNotice={failAuthNotice}
                />
                <TopLevelErrorContainer
                  className={authCss.largeSection}
                  errorMessages={
                    !pristine || touched ? { ...errors, ...submitErrors } : {}
                  }
                  isPartner={isPartner}
                  fieldOrder={fieldOrder}
                  touchedFields={touched}
                />
                {isPartner && <AuthSectionDivider />}
                <div className={authCss.section}>
                  {!isPartner && (
                    <div className={authCss.fieldSection}>
                      <div className="row">
                        <div className="col-xs-12">
                          <Field
                            component={Input}
                            hintStyle={hintStyle}
                            className={classNames(
                              authCss.inputField,
                              'nameField',
                            )}
                            dataTestId="name"
                            name="name"
                            id="suName"
                            validate={isValidFullName}
                            hintText={name || getLocalizedText(NAME)}
                            isBold
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div
                    className={classNames(authCss.fieldSection, {
                      [authCss.isPartnerFlow]: isPartner,
                    })}
                  >
                    <div className="row">
                      <div className="col-xs-12">
                        <Field
                          component={Input}
                          className={classNames(
                            authCss.inputField,
                            'emailField',
                            {
                              [authCss.isPartnerFlow]: isPartner,
                            },
                          )}
                          dataTestId="email"
                          hintText={
                            !isPartner
                              ? email ||
                                getRequiredLabel(
                                  getLocalizedText(EMAIL_ADDRESS),
                                )
                              : ''
                          }
                          name="email"
                          id="suEmail"
                          autoCorrect="off"
                          autoCapitalize="none"
                          label={
                            isPartner
                              ? email || getLocalizedText(EMAIL_ADDRESS)
                              : undefined
                          }
                          isRequired
                          validate={composeValidators(
                            isRequired(USERNAME_EMAIL_REQUIRED),
                            isValidEmail,
                          )}
                          {...additionalFieldProps}
                        />
                        {isPartner && validFieldsMap?.email && (
                          <Checkmark {...svgProps} />
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    className={classNames(authCss.fieldSection, {
                      [authCss.isPartnerFlow]: isPartner,
                    })}
                  >
                    <div className="row">
                      <div className="col-xs-12">
                        <Field
                          component={Input}
                          className={classNames(
                            authCss.inputField,
                            'passwordField',
                            {
                              [authCss.isPartnerFlow]: isPartner,
                            },
                          )}
                          hintText={
                            !isPartner
                              ? password ||
                                getRequiredLabel(getLocalizedText(PASSWORD))
                              : ''
                          }
                          dataTestId="password"
                          type="password"
                          name="password"
                          id="suPassword"
                          label={
                            isPartner
                              ? password || getLocalizedText(PASSWORD)
                              : undefined
                          }
                          isRequired
                          validate={composeValidators(
                            isRequired(PASSWORD_REQUIRED),
                            isValidPassword,
                          )}
                          {...additionalFieldProps}
                          inputStyle={
                            isPartner
                              ? {
                                  ...inputMappedPartnerAuthStyles.inputStyle,
                                  paddingBottom: '5px',
                                }
                              : undefined
                          }
                        />
                        {isPartner && validFieldsMap?.password && (
                          <Checkmark {...svgProps} />
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    className={classNames(authCss.fieldSection, {
                      [authCss.isPartnerFlow]: isPartner,
                    })}
                  >
                    <div className="row">
                      <div className="col-xs-12">
                        <Field
                          component={Input}
                          hintStyle={hintStyle}
                          className={classNames(
                            authCss.inputField,
                            'birthYearField',
                            {
                              [authCss.isPartnerFlow]: isPartner,
                            },
                          )}
                          dataTestId="birthYear"
                          name="birthYear"
                          id="suBirthYear"
                          hintText={
                            !isPartner
                              ? birthYear ||
                                getRequiredLabel(getLocalizedText(BIRTH_YEAR))
                              : ''
                          }
                          label={
                            isPartner
                              ? birthYear || getLocalizedText(BIRTH_YEAR)
                              : undefined
                          }
                          isRequired
                          validate={composeValidators(
                            isRequired(BIRTH_YEAR_REQUIRED),
                            isValidBirthYear,
                          )}
                          normalize={numericInputNormalizer(4)}
                          {...additionalFieldProps}
                        />
                        {isPartner && validFieldsMap?.birthYear && (
                          <Checkmark {...svgProps} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={authCss.section}>
                  <div className="row">
                    <div className="col-xs-12">
                      <div
                        className={classNames(css.signUpOptions, {
                          [css.isPartnerFlow]: isPartner,
                        })}
                      >
                        <Field
                          name="gender"
                          type="radio"
                          dataTestId="genderMale"
                          className={classNames(css.signUpOption, {
                            [css.isPartnerFlow]: isPartner,
                          })}
                          labelClassName={
                            isPartner && css.partnerFlowRadioButton
                          }
                          value="m"
                          label={getLocalizedText(MALE_LABEL)}
                          render={RadioButtonWrapper}
                        />
                        <Field
                          name="gender"
                          type="radio"
                          dataTestId="genderFemale"
                          className={classNames(css.signUpOption, {
                            [css.isPartnerFlow]: isPartner,
                          })}
                          labelClassName={
                            isPartner && css.partnerFlowRadioButton
                          }
                          value="f"
                          label={getLocalizedText(FEMALE_LABEL)}
                          render={RadioButtonWrapper}
                        />
                        <Field
                          name="gender"
                          type="radio"
                          dataTestId="genderNonBinary"
                          className={classNames(css.signUpOption, {
                            [css.isPartnerFlow]: isPartner,
                          })}
                          labelClassName={
                            isPartner && css.partnerFlowRadioButton
                          }
                          value="x"
                          label={getLocalizedText(NONBINARY_LABEL)}
                          render={RadioButtonWrapper}
                        />
                        <Field
                          name="gender"
                          type="radio"
                          dataTestId="genderPreferNotToSay"
                          className={classNames(css.signUpOption, {
                            [css.isPartnerFlow]: isPartner,
                          })}
                          labelClassName={
                            isPartner && css.partnerFlowRadioButton
                          }
                          value=""
                          label={getLocalizedText(PREFERNOTTOSAY_LABEL)}
                          render={RadioButtonWrapper}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className={authCss.section}>
                  <div className="row">
                    <div className="col-xs-12">
                      <CoralButton
                        id="signUpButton"
                        type="submit"
                        label={getLocalizedText(isPaymentFlow ? NEXT : SIGN_UP)}
                        isDisabled={
                          pristine ||
                          hasValidationErrors ||
                          submitting ||
                          submitSucceeded
                        }
                        className={classNames(authCss.authButton, {
                          [authCss.isPartnerFlow]: isPartner,
                        })}
                      />
                      <SecondaryContent
                        key="signUpSecondaryContent"
                        isPartnerFlow={isPartner}
                        handler={handleChangeToSignIn}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          );
        }}
      </Form>
    );
  }
}

export function mapStateToProps(state) {
  return {
    fakeAuthInfo: state.fakeAuth,
  };
}

const mapDispatchToProps = {
  storeAuthData: storeFakeAuthData,
  logSignUpClick,
};

export default connect(mapStateToProps, mapDispatchToProps)(SignUpView);
